import React from 'react'
import { observer } from 'mobx-react'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import CallMediaSettingsModalViewModel from 'messaging/calling/CallMediaSettingsModalViewModel'
import { FormGroup } from 'components/inputs'
import Icon from 'components/Icon'
import cx from 'classnames'
import styles from './CallMediaSettingsModal.sass'
import { truncate } from 'lodash'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetHeader,
} from 'components/ModalSheet'
import { lerp } from 'utils/math-util'
import { useActivation } from 'hooks/mobx-hooks'

interface Props {
  vm: CallMediaSettingsModalViewModel
}

export default observer(function CallMediaSettingsModal({ vm }: Props) {
  return (
    <ModalSheet showing={vm.showing} onRequestClose={() => vm.hide()}>
      <CallSettingsModalContent vm={vm} />
    </ModalSheet>
  )
})

const CallSettingsModalContent = observer(function CallSettingsModalContent({
  vm,
}: Props) {
  useActivation(vm)
  return (
    <ModalSheetContentBlock>
      <ModalSheetHeader withCloseButton>
        <ModalSheetContentTitle>Settings</ModalSheetContentTitle>
        <ModalSheetContentDescription>
          Test your audio and video for call
        </ModalSheetContentDescription>
      </ModalSheetHeader>
      <ModalSheetContent>
        <FormGroup label="Audio Settings">
          <RenderMediaSelect
            selectMediaDevice={vm.selectMediaDevice}
            devices={vm.audioInputDevices}
            selectedDevice={vm.selectedAudioInputDevice}
            icon={<Icon name="call-mic-on" />}
          />
          {vm.selectedAudioInputDevice && vm.previewAudioStream && (
            <MicrophoneCheck vm={vm} />
          )}
        </FormGroup>
        <div className={styles.videoDeviceSelect}>
          <FormGroup
            className={styles.videoDeviceSelectInput}
            label="Video settings"
          >
            <RenderMediaSelect
              className={styles.videoDeviceSelectInput}
              selectMediaDevice={vm.selectMediaDevice}
              devices={vm.videoInputDevices}
              selectedDevice={vm.selectedVideoInputDevice}
              icon={<Icon name="call-video-on" />}
            />
          </FormGroup>
          {vm.selectedVideoInputDevice && vm.previewVideoStream && (
            <CameraPreview mediaStream={vm.previewVideoStream} />
          )}
        </div>
      </ModalSheetContent>
    </ModalSheetContentBlock>
  )
})

interface MediaSelectProps {
  selectMediaDevice: (device?: MediaDeviceInfo) => Promise<void>
  devices: Array<MediaDeviceInfo>
  selectedDevice?: MediaDeviceInfo | null
  icon?: null | undefined | React.ReactNode
  className?: string
}

const RenderMediaSelect = observer(function RenderMediaSelect({
  selectMediaDevice,
  devices,
  selectedDevice,
  icon,
}: MediaSelectProps) {
  const itemSource = React.useMemo(() => ItemSource.static(devices), [devices])
  const selectProps = useCustomSelectStateFromItemSource({
    itemSource,
  })
  return (
    <div className={cx(styles.input)}>
      <CustomSelect
        {...selectProps}
        icon={icon}
        value={selectedDevice ?? undefined}
        onChange={(device: MediaDeviceInfo | undefined) =>
          selectMediaDevice(device)
        }
        itemToString={(device) => truncate(device.label, { length: 50 })}
        multi={false}
      />
    </div>
  )
})

interface MicrophoneCheckProps {
  vm: CallMediaSettingsModalViewModel
}

const numOvals = 36
const ovals = new Array(numOvals).fill(0)
const MicrophoneCheck = observer(function MicrophoneCheck({
  vm,
}: MicrophoneCheckProps) {
  const normalizedLevel = React.useMemo(
    () => lerp(0, numOvals, vm.audioLoudness - 32) / 256,
    [vm.audioLoudness]
  )

  return (
    <div className={styles.audioLevelVizRoot}>
      <div className={styles.dotsContainer}>
        {ovals.map((_, index) => {
          return (
            <svg width="100%" viewBox="0 0 100 100" key={index}>
              <circle
                cx="50"
                cy="50"
                r="50"
                fill={index < normalizedLevel ? '#2AB27B' : '#DFE2E5'}
              />
            </svg>
          )
        })}
      </div>
    </div>
  )
})

const CameraPreview = observer(function CameraPreview({
  mediaStream,
}: {
  mediaStream: MediaStream
}) {
  const ref = React.useRef<HTMLVideoElement | null>(null)
  React.useEffect(() => {
    if (mediaStream && ref.current) {
      const current = ref.current
      current.srcObject = mediaStream
      return () => {
        current.srcObject = null
      }
    }
  }, [mediaStream])

  if (!mediaStream) {
    return null
  }

  return (
    <div className={styles.cameraPreviewContainer}>
      <video autoPlay playsInline className={cx(styles.video)} ref={ref} />
    </div>
  )
})
